import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, forkJoin, Observable, takeUntil, take } from 'rxjs';
import { Validators, FormBuilder, FormArray, FormGroup } from '@angular/forms';
import { LoginService } from 'src/app/services/login.service';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { LookupService } from 'src/app/services/lookup.service';
import { AgencyService } from 'src/app/services/agency.service';

@Component({
  selector: 'ness-account-register',
  templateUrl: './account-register.component.html'
})
export class AccountRegisterComponent implements OnInit, OnDestroy {
  fieldDivisions$: Observable<ServerModels.ENESS_REF_FieldDivisions[]> = this.loginService.fieldDivisions$;
  filteredAgencies!: ServerModels.ENESS_REF_Agency[];
  filteredRoles!: ServerModels.ENESS_ApplicationRole[];
  filteredJobTitles!: ServerModels.ENESS_REF_JobTitles[];
  filteredFDGroup!: ServerModels.ENESS_REF_IntelligenceReportATFReferralGroups[];
  formTitle!: string | undefined;
  description!: string | undefined;
  registrationForm: FormGroup = this.fb.group({
    FirstName: ['', Validators.required],
    LastName: ['', Validators.required],
    Email: [{ value: '', disabled: true }, Validators.required],
    PhoneNumber: '',
    Roles: this.fb.array([]),
    UserTypeID: ['', Validators.required],
    JobTitleID: [{ value: '', disabled: true }, Validators.required],
    OtherJobTitle: '',
    AgencyID: { value: '', disabled: true },
    StateID: '',
    FieldDivisionID: '',
    FDGroupID: [{ value: '', disabled: true }]
  });
  requestSubmitted: boolean = false;
  userId!: string | undefined;
  userInfo!: ServerModels.UserInfoViewModel;
  userTypes: ServerModels.ENESS_REF_UserType[] = [];
  jobTitles: ServerModels.ENESS_REF_JobTitles[] = [];
  fdGroups: ServerModels.ENESS_REF_IntelligenceReportATFReferralGroups[] = [];

  private _formData!: ServerModels.RegisterViewModel;
  private _roles: ServerModels.ENESS_ApplicationRole[] = [];
  private _showAgency!: boolean;
  private _showFieldDivision!: boolean;
  private _showOtherJobTitle!: boolean;
  private _unsubscribe$: Subject<void> = new Subject<void>();

  get Roles(): FormArray {
    return this.registrationForm.controls['Roles'] as FormArray;
  }

  constructor(private fb: FormBuilder,
    private loginService: LoginService,
    public lookupService: LookupService,
    private agencyService: AgencyService,
    private router: Router) {
    this.loginService.userInfoData$.pipe(takeUntil(this._unsubscribe$)).subscribe({
      next: (info: ServerModels.UserInfoViewModel) => this.userInfo = info
    });
  }

  ngOnInit() {
    forkJoin({
      claims: this.loginService.getUserClaims(),
      role: this.loginService.getAllRoles(),
      userType: this.loginService.getUserTypes(),
      jobTitle: this.loginService.getJobTitles(),
      group: this.loginService.getAllFieldDivisionGroups(),
      userInfoData: this.loginService.getUserInfo()
    }).pipe(takeUntil(this._unsubscribe$)).subscribe({
      next: (val: { claims: Claims.Claim[], role: ServerModels.ENESS_ApplicationRole[], userType: ServerModels.ENESS_REF_UserType[], jobTitle: ServerModels.ENESS_REF_JobTitles[], group: ServerModels.ENESS_REF_IntelligenceReportATFReferralGroups[], userInfoData: ServerModels.UserInfoViewModel }) => {
          this.userInfo = val.userInfoData;
          if (this.userInfo != null && this.userInfo.ApprovalStatus?.toLowerCase() == 'denied') {
            this.userId = this.userInfo.Id;
            this.formTitle = 'Update Registration';
            this.description = 'Please update your registration information and resubmit for approval.';
          } else if (this.userInfo != null && this.userInfo.Deleted == true) {
            this.userId = this.userInfo.Id;
            this.formTitle = 'Update Registration';
            this.description = 'Please update your registration information and resubmit for approval.';
          } else {
            this.formTitle = 'NESS New User Registration';
            this.description = 'Please complete the following information for the NESS registration process.';
            this.setUserClaims(val.claims);
          }
          this._roles = val.role;
          this.filteredRoles = val.role;
          this.userTypes = val.userType;
          this.jobTitles = val.jobTitle;
          this.fdGroups = val.group;
        if (this.userInfo) {
            this.updateAgency(false);
            this.updateFieldDivision(false);
            let roles = [];
            if (this.userInfo.Roles && this.userInfo.Roles.length > 0) {
              this.userInfo.Roles.forEach(r => {
                if (r.Name != 'NESS Administrator' && r.Name != 'RMS Case Selection' && r.Name != 'CDS Approver') {
                  roles.push(this.fb.group({ Name: [r.Name, Validators.required] }));
                }
              });
            } else {
              roles.push(this.fb.group({ Name: ['', Validators.required] }));
            }
            
            this.registrationForm.setControl('Roles', this.fb.array(roles));
            if (this.userInfo.Agency != null && this.userInfo.Agency.AgencyID != null) {
              this._showAgency = true;
              const agencyCtrl = this.registrationForm.get('AgencyID');
              agencyCtrl.reset({ value: '', disabled: false });
              this.agencyService.getAgenciesForState(this.userInfo.Agency.ENESS_REF_Counties.StateID).pipe(take(1)).subscribe((agencies: ServerModels.ENESS_REF_Agency[]) => {
                this.filteredAgencies = agencies;
              });
            }

            this.updateJobTitles(this.userInfo.UserTypeID);
            if (this.userInfo.UserType != null && this.userInfo.UserType.UserTypeName == 'ATFFieldDivision') {
              this.updateFDGroups(this.userInfo.FieldDivisionID)
            }
            
            this.registrationForm.patchValue({
              FirstName: this.userInfo.FirstName,
              LastName: this.userInfo.LastName,
              Email: this.userInfo.Email,
              PhoneNumber: this.userInfo.PhoneNumber,
              UserTypeID: this.userInfo.UserTypeID,
              JobTitleID: this.userInfo.JobTitleID ?? "",
              OtherJobTitle: this.userInfo.OtherJobTitle,
              AgencyID: (this.userInfo.Agency ? this.userInfo.Agency.AgencyID : null),
              StateID: (this.userInfo.Agency ? this.userInfo.Agency.ENESS_REF_Counties.StateID : null),
              FieldDivisionID: (this.userInfo.UserType != null && this.userInfo.UserType.UserTypeName == 'ATFFieldDivision' ? this.userInfo.FieldDivisionID : null),
              FDGroupID: this.userInfo.FieldDivisionGroupID ?? ""
            });

            let selectJobTitle = this.jobTitles.find(j => j.ID == this.userInfo.JobTitleID);
            if (selectJobTitle && selectJobTitle.JobTitle === 'Other') {
              this.registrationForm.get('OtherJobTitle').setValidators(Validators.required);
              this._showOtherJobTitle = true;
            }

            if (this.userInfo.UserType != null && this.userInfo.UserType.UserTypeName == 'ATFFieldDivision') {
              this._showFieldDivision = true;
            }

            if (this.userInfo.UserType != null && this.userInfo.UserType.UserTypeName == 'StateLocalLE') {
              this.updateRoles(false);
            }

            
          } else {
            this.router.navigate(['/login']);
          }

        },
        error: (err:any) => console.log(err)
      });

  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  get roles(): ServerModels.ENESS_ApplicationRole[] {
    return this._roles;
  }

  get showAgency(): boolean {
    return this._showAgency;
  }

  get showFieldDivision(): boolean {
    return this._showFieldDivision;
  }

  get showOtherJobTitle(): boolean {
    return this._showOtherJobTitle;
  }

  public onReset() {
    this._showAgency = false;
    this._showFieldDivision = false;
    this._showOtherJobTitle = false;
    this.updateAgency(false);
    this.updateFieldDivision(false);

    this.registrationForm.reset({
      'LastName': '',
      'FirstName': '',
      'PhoneNumber': '',
      'Roles': [{ 'Name': '' }],
      'UserTypeID': '',
      'JobTitleID': '',
      'OtherJobTitle': '',
      'FDGroupID': '',
      'Email': { value: this.userInfo.Email, disabled: true }
    });
  }

  public onSelectState(stateId: string): void {
    const agencyCtrl = this.registrationForm.get('AgencyID');
    agencyCtrl.reset({ value: '', disabled: (stateId === '') });
    this.agencyService.getAgenciesForState(stateId).pipe(take(1)).subscribe((agencies: ServerModels.ENESS_REF_Agency[]) => {
      this.filteredAgencies = agencies;
    });
    this.registrationForm.updateValueAndValidity();
  }

  public onSelectUserType(userTypeId: string): void {
    const selectedUserType = this.userTypes.find(ut => ut.Id === userTypeId);
    this.updateFieldDivision(selectedUserType && selectedUserType.UserTypeName === 'ATFFieldDivision');
    this.updateAgency(selectedUserType && selectedUserType.UserTypeName === 'StateLocalLE');
    const canSelectPowerUser = selectedUserType && selectedUserType.UserTypeName !== 'StateLocalLE';
    this.updateRoles(canSelectPowerUser);
    this.updateJobTitles(userTypeId);
  }

  public onSelectFieldDivision(fieldDivisionId: string) {
    this.updateFDGroups(fieldDivisionId);
  }

  public onSelectJobTitle(jobTitleId: string) {
    let selectJobTitle = this.jobTitles.find(j => j.ID == jobTitleId);

    if (selectJobTitle && selectJobTitle.JobTitle === 'Other') {
      this.registrationForm.get('OtherJobTitle').setValidators(Validators.required);
      this._showOtherJobTitle = true;
    }
    else {
      this.registrationForm.get('OtherJobTitle').clearValidators();
      this._showOtherJobTitle = false;
    }
    this.registrationForm.get('OtherJobTitle').setValue('');
    this.registrationForm.updateValueAndValidity();
  }

  public registerUser(): void {
    this.requestSubmitted = false;
    if (this.registrationForm.value != null && this.registrationForm.valid) {
      this._formData = Object.assign({}, this.registrationForm.getRawValue());

      this._formData.UserId = this.userId == '' ? null : this.userId;
      this.loginService.registerNewUser(this._formData).pipe(takeUntil(this._unsubscribe$)).subscribe({
        next: (resp: ServerModels.RegisterViewModel) => {
          this.requestSubmitted = true;
        },
        complete: () => {
          sessionStorage.removeItem(environment.authTokenStorageKey);
          sessionStorage.removeItem(environment.authRefreshTokenStorageKey);
          sessionStorage.removeItem(environment.authTokenExpiresStorageKey);
          this.onReset();
        },
        error: (err:any) => {
          this.requestSubmitted = false;
        }
      });
    }
  }

  private setUserClaims(claims: Claims.Claim[]): void {
    if (claims) {
      let email = claims.find(claim => claim.Type?.toLowerCase() == 'email' || claim.Type === 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier');
      if (email == null) {
        sessionStorage.removeItem(environment.authTokenStorageKey);
        sessionStorage.removeItem(environment.authRefreshTokenStorageKey);
      }
      let userInfo = {
        FirstName: null,
        LastName: null,
        Email: (email == null ? null : email.Value),
        Agency: {
          AgencyID: null, AgencyName: null, CreatedOn: null, ENESS_REF_Counties: { StateID: null }
        },
        UserTypeID: '',
        FieldDivisionID: '',
        PhoneNumber: null,
        PhoneNumberConfirmed: null,
        Disabled: null,
        Deleted: null,
        TwoFactorEnabled: null,
        Roles: [],
        ApprovalStatus: null
      } as ServerModels.UserInfoViewModel;
      this.loginService.nextUserInfo(userInfo);
    }
  }

  private updateAgency(isSelected: boolean): void {
    this._showAgency = isSelected;
    if (isSelected) {
      this.registrationForm.get('AgencyID').setValidators(Validators.required);
      this.registrationForm.get('StateID').setValidators(Validators.required);
    } else {
      this.registrationForm.get('AgencyID').clearValidators();
      this.registrationForm.get('StateID').clearValidators();
    }

    this.registrationForm.get('StateID').setValue('');
    this.registrationForm.get('AgencyID').reset({ value: '', disabled: true });
    this.registrationForm.updateValueAndValidity();
  }

  private updateFieldDivision(isSelected: boolean): void {
    this._showFieldDivision = isSelected;
    if (isSelected) {
      this.registrationForm.get('FieldDivisionID').setValidators(Validators.required);
      this.registrationForm.get('FDGroupID').setValidators(Validators.required);
    } else {
      this.registrationForm.get('FieldDivisionID').clearValidators();
      this.registrationForm.get('FDGroupID').clearValidators();
    }

    this.registrationForm.get('FieldDivisionID').setValue('');
    this.registrationForm.get('FDGroupID').setValue('');
    this.registrationForm.updateValueAndValidity();
  }

  private updateRoles(canSelectPowerUser: boolean): void {
    if (canSelectPowerUser) {
      this.filteredRoles = this.roles;
    } else {
      this.filteredRoles = this.roles.filter(r => r.Name !== 'Power User');
    }
    this.registrationForm.updateValueAndValidity();
  }

  private updateJobTitles(userTypeId: string) {
    const jobTitleCtrl = this.registrationForm.get('JobTitleID');
    if (userTypeId != "") {
      this.filteredJobTitles = this.jobTitles.filter(j => j.UserTypeID == userTypeId);
      jobTitleCtrl.reset({ value: '', disabled: false });
    }
    else {
      jobTitleCtrl.reset({ value: '', disabled: true });
    }
    this.registrationForm.get('OtherJobTitle').setValue('');
    this.registrationForm.get('OtherJobTitle').clearValidators();
    this._showOtherJobTitle = false;
    this.registrationForm.updateValueAndValidity();
  }

  private updateFDGroups(fieldDivisionId: string) {
    const fdGroupCtrl = this.registrationForm.get('FDGroupID');
    if (fieldDivisionId != "") {
      this.filteredFDGroup = this.fdGroups.filter(g => g.FieldDivisionID == fieldDivisionId);
      fdGroupCtrl.reset({ value: '', disabled: false });
    }
    else {
      fdGroupCtrl.reset({ value: '', disabled: true });
    }
    this.registrationForm.updateValueAndValidity();
  }
}
